import { authHeaders } from '../../lib/authHeaders';

export interface PushDryRunResponse {
  tags: string[];
  summary: {
    parties: number;
    /** Parties whose Close lead will be created fresh (no prior push). */
    would_create?: number;
    /** Parties already in Close — their existing lead will get this
     *  batch's tag added; no duplicate created. */
    would_link?: number;
    with_phone: number;
    with_address: number;
    without_contact: number;
    with_enriched_lease?: number;
  };
  preview_warnings?: string[];
  briefs: Array<{
    party_id: string;
    display_name: string;
    /** When true, this party already has a Close lead from a prior
     *  push. The real push will tag-add to that existing lead instead
     *  of creating a new one. */
    already_in_close?: boolean;
    markdown?: string;
    lead_custom_fields_preview?: Record<string, string | number>;
    lease_source_doc_number?: string | null;
  }>;
  dry_run: true;
}

export interface PushLiveResponse {
  build_batch_id: string;
  tags: string[];
  summary: {
    parties: number;
    /** Fresh Close lead inserts — actual new records in Close. */
    leads_created: number;
    /** Already-pushed parties whose existing Close lead got this
     *  batch's tag added. No duplicate created in Close; call history
     *  and notes on the original lead are preserved. */
    leads_linked: number;
    /** Always 0 in v0.1 — push creates Lead only. v0.2 wires opportunity create as a manual action. */
    opportunities_created?: number;
    with_phone: number;
    with_address: number;
    without_contact: number;
  };
  close_leads: Array<{
    party_id: string;
    close_lead_id: string;
    /** Always null in v0.1 per the Lead-only push policy. */
    close_opportunity_id: string | null;
    close_lead_url: string;
    /** true when this is an already-existing Close lead that was
     *  tag-added to this batch's Smart View; false when freshly
     *  created. */
    linked: boolean;
  }>;
  push_warnings: string[];
  /** Close Smart View (saved_search) created for this batch. null when
   *  no leads landed, or when CLOSE_LEAD_FIELDS.timberline_batch_id has
   *  not been populated, or when Close 5xx'd the saved_search create. */
  smart_view: {
    id: string;
    name: string;
    url: string;
  } | null;
  /** When smart_view is null but the field is provisioned: the operator
   *  can paste this query string into Close's "Save search" UI. */
  smart_view_fallback_query: string | null;
  dry_run: false;
}

// Two request shapes feed close-push:
// - PushBodySingleSource: from an AOI or pool order page (current).
// - PushBodyLeadList:    from the /mi/leads bulk action.
// The endpoint dispatches on source_kind.
export interface PushBodySingleSource {
  source_kind: 'aoi' | 'pool_order';
  source_id: string;
  party_ids: string[];
  options?: { dry_run?: boolean; include_brief?: boolean };
}
export interface PushBodyLeadList {
  source_kind: 'lead_list';
  lead_list_item_ids: string[];
  options?: { dry_run?: boolean; include_brief?: boolean };
}
export type PushBody = PushBodySingleSource | PushBodyLeadList;

async function postJson<T>(path: string, body: any): Promise<T> {
  const r = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

function withOptions<T extends PushBody>(b: Omit<T, 'options'>, dry_run: boolean): T {
  return { ...(b as any), options: { dry_run, include_brief: true } };
}

export const closePushApi = {
  dryRun: (b: Omit<PushBodySingleSource, 'options'> | Omit<PushBodyLeadList, 'options'>) =>
    postJson<PushDryRunResponse>('/api/mi/close-push', withOptions(b as any, true)),
  push: (b: Omit<PushBodySingleSource, 'options'> | Omit<PushBodyLeadList, 'options'>) =>
    postJson<PushLiveResponse>('/api/mi/close-push', withOptions(b as any, false)),
};
